import { captureSupportScope, internalApiHeaders } from '../admin/supportTransport';

const API_BASE = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

export interface CompanyBusinessProfile {
  name: string;
  size: string;
  industry: string;
  website: string;
  description: string;
  offerings: string[];
  customerSegments: string[];
  customerNeeds: string[];
  serviceAreas: string[];
  businessModel: string;
  channels: string[];
  differentiation: string[];
  capabilities: string[];
  proofPoints: string[];
  existingMission: string;
  existingVision: string;
  existingValues: string[];
  statedGoals: string[];
  constraints: string[];
}
export type ProfileField = keyof CompanyBusinessProfile;
export interface ContextSource {
  id: string;
  kind: 'website' | 'document';
  label: string;
  url: string | null;
  fileName: string | null;
  mimeType: string | null;
  byteSize: number | null;
  status: 'pending' | 'processing' | 'ready' | 'failed' | 'deleted';
  error: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  canDownload: boolean;
  includedInContext: boolean;
  excerpt?: string | null;
}
export interface CompanyContextFact {
  id: string;
  field: ProfileField;
  value: string | string[];
  origin: 'source' | 'interview' | 'manual';
  sourceIds: string[];
  inference: boolean;
}
export interface InterviewQuestion {
  id: string;
  field: ProfileField;
  prompt: string;
  helperText?: string;
  reason?: string;
  choices?: Array<{ id: string; label: string; description?: string }>;
}
export interface OnboardingResult {
  companyId: string;
  linkId?: string;
  acceptUrl?: string;
  ownerInviteUrl?: string;
}
export interface OnboardingDraft {
  id: string;
  mode: 'company' | 'adviser' | 'existing';
  practiceId: string | null;
  ownerEmail?: string | null;
  pendingAnswer?: { questionId: string; value: string } | null;
  companyId: string | null;
  status: 'draft' | 'queued' | 'researching' | 'ready' | 'failed' | 'finalizing' | 'completed';
  revision: number;
  profile: CompanyBusinessProfile;
  sources: ContextSource[];
  facts: CompanyContextFact[];
  interview: {
    answeredCount: number;
    maxQuestions: number;
    currentQuestion: InterviewQuestion | null;
    complete: boolean;
  };
  warnings: string[];
  expiresAt: string;
  researchRuns: number;
  aiCostCents: number;
  progress?: { stage?: string; extractOnly?: boolean; [key: string]: unknown };
  result?: OnboardingResult;
}
export interface OnboardingCapabilities {
  enabled: boolean;
  documentsEnabled: boolean;
  maxInterviewQuestions: number;
  maxDocuments: number;
  maxDocumentBytes: number;
  maxTotalDocumentBytes: number;
}
export interface CompanyContext {
  companyId: string;
  revision: number;
  profile: CompanyBusinessProfile;
  facts: CompanyContextFact[];
  unknowns: string[];
  sources: ContextSource[];
  updatedAt: string;
  activeDraftId: string | null;
}

export class ContextApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ContextApiError';
  }
}

function apiUrl(path: string) {
  return `${API_BASE}${API_BASE.endsWith('/api') ? path.replace(/^\/api(?=\/)/, '') : path}`;
}
async function authenticatedFetch(path: string, options: RequestInit = {}) {
  const scope = captureSupportScope();
  const token = await (window as any).Clerk?.session?.getToken?.();
  const destination = apiUrl(path);
  const headers = internalApiHeaders(
    destination,
    {
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    false,
    scope
  );
  try {
    return await fetch(destination, { ...options, headers });
  } catch {
    throw new ContextApiError(
      'We couldn’t reach StratPlan. Check your connection and try again.',
      0
    );
  }
}
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await authenticatedFetch(path, options);
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.success) {
    throw new ContextApiError(
      body?.error || 'Unable to load company context. Please try again.',
      response.status
    );
  }
  return body.data as T;
}
const json = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) });
const draftPath = (id: string) => `/api/company-onboarding/${encodeURIComponent(id)}`;
const contextPath = (id: string) => `/api/company-context/${encodeURIComponent(id)}`;
export function cleanCompanyProfile(profile: CompanyBusinessProfile): CompanyBusinessProfile {
  return Object.fromEntries(
    Object.entries(profile).map(([field, value]) => [
      field,
      Array.isArray(value) ? value.map((item) => item.trim()).filter(Boolean) : value.trim(),
    ])
  ) as unknown as CompanyBusinessProfile;
}

export const companyOnboardingApi = {
  capabilities: () => request<OnboardingCapabilities>('/api/company-onboarding/capabilities'),
  list: () => request<OnboardingDraft[]>('/api/company-onboarding'),
  remove: (id: string) => request<{ removed: boolean }>(draftPath(id), { method: 'DELETE' }),
  cancel: (id: string, expectedRevision: number) =>
    request<OnboardingDraft>(`${draftPath(id)}/cancel`, json('POST', { expectedRevision })),
  create: (body: {
    mode: 'company' | 'adviser';
    practiceId?: string;
    profile?: Partial<CompanyBusinessProfile>;
  }) => request<OnboardingDraft>('/api/company-onboarding', json('POST', body)),
  get: (id: string) => request<OnboardingDraft>(draftPath(id)),
  save: (
    id: string,
    expectedRevision: number,
    profile: CompanyBusinessProfile,
    ownerEmail?: string,
    pendingAnswer?: { questionId: string; value: string } | null
  ) =>
    request<OnboardingDraft>(
      draftPath(id),
      json('PATCH', {
        expectedRevision,
        profile: cleanCompanyProfile(profile),
        ...(ownerEmail !== undefined ? { ownerEmail } : {}),
        ...(pendingAnswer !== undefined ? { pendingAnswer } : {}),
      })
    ),
  research: (id: string, expectedRevision: number, website: string) =>
    request<OnboardingDraft>(
      `${draftPath(id)}/research`,
      json('POST', { expectedRevision, website })
    ),
  upload: (id: string, expectedRevision: number, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<OnboardingDraft>(
      `${draftPath(id)}/sources?expectedRevision=${expectedRevision}`,
      { method: 'POST', body }
    );
  },
  removeSource: (id: string, sourceId: string, expectedRevision: number) =>
    request<OnboardingDraft>(
      `${draftPath(id)}/sources/${encodeURIComponent(sourceId)}`,
      json('DELETE', { expectedRevision })
    ),
  nextQuestion: (id: string, expectedRevision: number) =>
    request<OnboardingDraft>(`${draftPath(id)}/interview/next`, json('POST', { expectedRevision })),
  answer: (
    id: string,
    expectedRevision: number,
    questionId: string,
    answer: string,
    skipped = false
  ) =>
    request<OnboardingDraft>(
      `${draftPath(id)}/interview/answer`,
      json('POST', { expectedRevision, questionId, answer, skipped })
    ),
  complete: (id: string, expectedRevision: number, ownerEmail?: string) =>
    request<OnboardingDraft | OnboardingResult>(
      `${draftPath(id)}/complete`,
      json('POST', { expectedRevision, ownerEmail })
    ),
};

export function applyOnboardingCompletion(
  current: OnboardingDraft,
  response: OnboardingDraft | OnboardingResult
): OnboardingDraft {
  const completed = 'status' in response ? response : null;
  return {
    ...current,
    ...completed,
    status: 'completed',
    revision: completed?.revision ?? current.revision + 1,
    profile: completed?.profile ?? current.profile,
    sources: completed?.sources ?? current.sources,
    facts: completed?.facts ?? current.facts,
    warnings: completed?.warnings ?? current.warnings,
    interview: {
      ...current.interview,
      ...completed?.interview,
      currentQuestion: null,
      complete: true,
    },
    pendingAnswer: null,
    result: completed ? completed.result : (response as OnboardingResult),
  };
}

export const companyContextApi = {
  get: (companyId: string) => request<CompanyContext>(contextPath(companyId)),
  save: (companyId: string, expectedRevision: number, profile: CompanyBusinessProfile) =>
    request<CompanyContext>(
      contextPath(companyId),
      json('PATCH', { expectedRevision, profile: cleanCompanyProfile(profile) })
    ),
  research: (companyId: string, expectedRevision: number, website?: string) =>
    request<OnboardingDraft>(
      `${contextPath(companyId)}/research`,
      json('POST', { expectedRevision, website })
    ),
  approve: (companyId: string, draftId: string, expectedRevision: number) =>
    request<CompanyContext>(
      `${contextPath(companyId)}/approve`,
      json('POST', { draftId, expectedRevision })
    ),
  upload: (companyId: string, expectedRevision: number, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<ContextSource>(
      `${contextPath(companyId)}/sources?expectedRevision=${expectedRevision}`,
      { method: 'POST', body }
    );
  },
  buildFromDocuments: (
    companyId: string,
    expectedRevision: number,
    files: File[],
    website?: string
  ) => {
    const body = new FormData();
    files.forEach((file) => body.append('files', file));
    if (website?.trim()) body.append('website', website.trim());
    return request<OnboardingDraft>(
      `${contextPath(companyId)}/sources/build?expectedRevision=${expectedRevision}`,
      { method: 'POST', body }
    );
  },
  includeSource: (
    companyId: string,
    sourceId: string,
    expectedRevision: number,
    includedInContext: boolean
  ) =>
    request<CompanyContext>(
      `${contextPath(companyId)}/sources/${encodeURIComponent(sourceId)}`,
      json('PATCH', { expectedRevision, includedInContext })
    ),
  replaceSource: (companyId: string, sourceId: string, expectedRevision: number, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<ContextSource>(
      `${contextPath(companyId)}/sources/${encodeURIComponent(sourceId)}/replace?expectedRevision=${expectedRevision}`,
      { method: 'POST', body }
    );
  },
  removeSource: (companyId: string, sourceId: string, expectedRevision: number) =>
    request<CompanyContext>(
      `${contextPath(companyId)}/sources/${encodeURIComponent(sourceId)}`,
      json('DELETE', { expectedRevision })
    ),
  download: async (companyId: string, source: ContextSource) => {
    const response = await authenticatedFetch(
      `${contextPath(companyId)}/sources/${encodeURIComponent(source.id)}/original`
    );
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ContextApiError(
        body?.error || 'Unable to download this document.',
        response.status
      );
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = source.fileName || source.label;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

export const emptyCompanyProfile: CompanyBusinessProfile = {
  name: '',
  size: '',
  industry: '',
  website: '',
  description: '',
  offerings: [],
  customerSegments: [],
  customerNeeds: [],
  serviceAreas: [],
  businessModel: '',
  channels: [],
  differentiation: [],
  capabilities: [],
  proofPoints: [],
  existingMission: '',
  existingVision: '',
  existingValues: [],
  statedGoals: [],
  constraints: [],
};

export const isResearching = (draft: OnboardingDraft | null) =>
  draft?.status === 'queued' || draft?.status === 'researching';

export const isOlderDraft = (current: OnboardingDraft | null, incoming: OnboardingDraft) =>
  current?.id === incoming.id && current.revision > incoming.revision;
